import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Button, Icon, Switch, Dialog, DialogBody, DialogFooter, DialogHeader, Text, Select, RadioGroup, Loader} from '@gravity-ui/uikit';
import {TrashBin, Plus} from '@gravity-ui/icons';
import {ActionModal, FormInput, useErrorHandler, ErrorModal} from '../../../../shared';
import {CommunicationMediaType, CommunicationVideoStyle} from '../../../../entities';
import {useSendMessageForm} from '../../model';
import {ImageUploadButton, PreparedImageData, fundraiserImageApi} from '../../../fundraiserImages';
import styles from './SendAppealNotificationModal.module.scss';

interface SendNotificationModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  appealId: string;
}

const MEDIA_TYPE_OPTIONS = [
  {value: 'image', content: 'Изображение'},
  {value: 'video', content: 'Видео'},
];

const VIDEO_STYLE_OPTIONS = [
  {value: 'Circle', content: 'Кружок'},
  {value: 'Rectangle', content: 'Вертикальное'},
];

export const SendAppealNotificationModal = ({open, setOpen, appealId}: SendNotificationModalProps) => {
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isVideoUploading, setIsVideoUploading] = useState(false);
  const videoInputRef = useRef<HTMLInputElement>(null);

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    resetForm,
    submitForm,
    isValid,
    isLoading,
    submitError,
  } = useSendMessageForm({appealId, setOpen});

  const {isErrorModalOpen, errorInfo, handleError, handleClose} = useErrorHandler({
    resourceName: 'Уведомление',
    title: 'Ошибка при отправке уведомления',
  });

  useEffect(() => {
    if (submitError) {
      handleError(submitError);
    }
  }, [submitError, handleError]);

  useEffect(() => {
    if (open) {
      resetForm();
      setIsVideoUploading(false);
    }
    // eslint-disable-next-line
  }, [open]);

  const handleImagePrepared = (data: PreparedImageData, previewUrl: string) => {
    setFieldValue('imageData', data);
    setFieldValue('imagePreview', previewUrl);
  };

  const handleRemoveImage = () => {
    setFieldValue('imageData', null);
    setFieldValue('imagePreview', null);
  };

  const handleVideoSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) return;
    const file = event.target.files[0];
    if (!file.type.startsWith('video/')) return;

    setIsVideoUploading(true);
    try {
      const response = await fundraiserImageApi.prepareUpload([file.type]);
      if (!response.items || response.items.length === 0 || !response.items[0].url) {
        throw new Error('Ошибка подготовки загрузки видео');
      }

      const uploadItem = response.items[0];
      const buffer = await file.arrayBuffer();
      const uploadResponse = await fetch(uploadItem.url, {
        method: 'PUT',
        headers: {'Content-Type': file.type},
        body: buffer,
      });

      if (!uploadResponse.ok) {
        throw new Error('Ошибка загрузки видео');
      }

      const previewUrl = URL.createObjectURL(file);
      setFieldValue('videoUploadId', uploadItem.id);
      setFieldValue('videoPreview', previewUrl);
    } catch (error) {
      console.error('Ошибка загрузки видео:', error);
    } finally {
      setIsVideoUploading(false);
      event.target.value = '';
    }
  }, [setFieldValue]);

  const handleRemoveVideo = () => {
    setFieldValue('videoUploadId', null);
    setFieldValue('videoPreview', null);
  };

  const handleMediaTypeChange = (value: string[]) => {
    const newType = value[0] as CommunicationMediaType;
    setFieldValue('mediaType', newType);
    if (newType === 'image') {
      setFieldValue('videoUploadId', null);
      setFieldValue('videoPreview', null);
    } else {
      setFieldValue('imageData', null);
      setFieldValue('imagePreview', null);
    }
  };

  const handleVideoStyleChange = (value: string) => {
    setFieldValue('videoStyle', value as CommunicationVideoStyle);
  };

  const handleSwitchChange = (field: string) => (checked: boolean) => {
    setFieldValue(field, checked, true);
  };

  const handleFormSubmit = () => {
    setIsConfirmModalOpen(true);
  };

  const handleConfirmSubmit = () => {
    setIsConfirmModalOpen(false);
    submitForm();
  };

  const handleCancelConfirm = () => {
    setIsConfirmModalOpen(false);
  };

  return (
    <>
      <ActionModal
        open={open}
        onClose={() => setOpen(false)}
        setOpen={() => setOpen(false)}
        resetForm={resetForm}
        submitForm={handleFormSubmit}
        isLoading={isLoading}
        size={'l'}
        buttonsSize={'l'}
        caption={'Отправить уведомление'}
        textButtonApply={'Отправить'}
        textButtonCancel={'Отменить'}
        viewActionButton={isValid ? 'action' : 'outlined'}>
        <div className={styles.form}>
          <FormInput
            label="Заголовок"
            placeholder="Заголовок уведомления"
            name="subject"
            value={values.subject}
            onChange={handleChange}
            onBlur={handleBlur}
            isRequired={true}
            error={touched.subject && errors.subject}
          />
          <FormInput
            label="Текст"
            isTextarea
            placeholder="Текст уведомления"
            name="body"
            value={values.body}
            onChange={handleChange}
            onBlur={handleBlur}
            isRequired={true}
            error={touched.body && errors.body}
          />

          <div className={styles.switchField}>
            <label className={styles.switchLabel}>Показывать кнопку</label>
            <Switch size="l" checked={values.showButton} onUpdate={handleSwitchChange('showButton')} />
          </div>

          {values.showButton && (
            <>
              <FormInput
                label="Текст кнопки"
                placeholder="Текст кнопки"
                name="buttonText"
                value={values.buttonText}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.buttonText && errors.buttonText}
              />

              <FormInput
                label="Ссылка кнопки"
                placeholder="https://example.com"
                name="buttonLink"
                value={values.buttonLink}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.buttonLink && errors.buttonLink}
              />
            </>
          )}

          <div className={styles.selectField}>
            <label className={styles.selectLabel}>Медиа</label>
            <Select
              size="xl"
              width="max"
              value={[values.mediaType]}
              onUpdate={handleMediaTypeChange}
              options={MEDIA_TYPE_OPTIONS}
            />
          </div>

          {values.mediaType === 'image' && (
            <div className={styles.imageField}>
              <label className={styles.imageLabel}>Изображение</label>
              <div className={styles.imageUpload}>
                {!values.imagePreview && (
                  <ImageUploadButton isCreating={true} aspectRatio={16 / 9} onPrepareImageData={handleImagePrepared} withBg />
                )}
                {values.imagePreview && (
                  <div className={styles.imageContainer}>
                    <div className={styles.imagePreview}>
                      <img src={values.imagePreview} alt="Превью" />
                      <div className={styles.imageActions}>
                        <Button
                          view="flat"
                          size="s"
                          onClick={handleRemoveImage}
                          className={styles.removeImageBtn}
                          title="Удалить изображение">
                          <Icon data={TrashBin} size={14} />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {values.mediaType === 'video' && (
            <>
              <div className={styles.selectField}>
                <label className={styles.selectLabel}>Видео</label>
                <RadioGroup
                  size="l"
                  value={values.videoStyle}
                  onUpdate={handleVideoStyleChange}
                  options={VIDEO_STYLE_OPTIONS}
                />
              </div>

              <div className={styles.imageField}>
                <label className={styles.imageLabel}>Файл видео</label>
                <div className={styles.videoUploadArea}>
                  {!values.videoPreview && !isVideoUploading && (
                    <>
                      <div className={values.videoStyle === 'Circle' ? styles.videoUploadCircle : styles.videoUploadRectangle}>
                        <Button view="normal" size="xl" onClick={() => videoInputRef.current?.click()}>
                          <Icon data={Plus} size={20} />
                        </Button>
                      </div>
                      <input
                        ref={videoInputRef}
                        type="file"
                        accept="video/mp4,video/webm"
                        style={{display: 'none'}}
                        onChange={handleVideoSelect}
                      />
                    </>
                  )}
                  {isVideoUploading && (
                    <div className={styles.videoLoading}>
                      <Loader size="s" />
                      <span>Загрузка видео...</span>
                    </div>
                  )}
                  {values.videoPreview && !isVideoUploading && (
                    <div className={styles.videoPreviewContainer}>
                      <div className={values.videoStyle === 'Circle' ? styles.videoPreviewCircle : styles.videoPreviewRectangle}>
                        <video src={values.videoPreview} className={styles.videoElement} muted />
                      </div>
                      <Button view="raised" size="xs" onClick={handleRemoveVideo} className={styles.removeVideoBtn}>
                        <Icon data={TrashBin} size={14} />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </ActionModal>

      <Dialog size="s" hasCloseButton open={isConfirmModalOpen} onClose={handleCancelConfirm}>
        <DialogHeader caption="Отправка уведомления" />
        <DialogBody>
          <Text variant="body-2" color="secondary">
            Нажимая «Подтвердить» вы отправите уведомление. Вы уверены?
          </Text>
        </DialogBody>
        <DialogFooter
          onClickButtonCancel={handleCancelConfirm}
          onClickButtonApply={handleConfirmSubmit}
          propsButtonApply={{size: 'l', loading: isLoading, view: 'action'}}
          propsButtonCancel={{size: 'l'}}
          textButtonApply="Подтвердить"
          textButtonCancel="Отменить"
        />
      </Dialog>

      <ErrorModal open={isErrorModalOpen} onClose={handleClose} title={errorInfo.title} message={errorInfo.message} />
    </>
  );
};
